import React, { createContext, useContext, useState, useCallback } from 'react';
import { apiClient } from '../services/api';
import { userflowEngine } from '../services/userflowEngine';
import { assistantManager } from '../services/assistantManager';
import { Step, StageId, AiFormSession } from '../scripts/userflow';
import { getAiFormMetadata } from '../services/aiFormRegistry';
import * as aiFormService from '../services/aiFormService';

interface UserflowContextType {
  // State
  isLoading: boolean;
  isAssistantOpen: boolean;
  currentMessage: string;
  currentStep: Step | null;
  currentForm: any | null;
  aiFormSession: AiFormSession | null;
  isAiFormAwaitingConfirmation: boolean;
  currentStage: StageId;
  rolesCount: number;
  usersCount: number;
  messageHistory: Array<{ type: 'ai' | 'user' | 'system'; content: string; timestamp: Date }>;

  // Actions
  openAssistant: () => void;
  closeAssistant: () => void;
  initializeAssistant: () => Promise<void>;
  executeNextStep: (userAction?: any) => Promise<void>;
  submitForm: (formData: any) => Promise<void>;
  selectConfirmOption: (optionValue: string) => Promise<void>;
  startAiFormSession: (metadataKey: string, endpoint: string, method: 'POST' | 'PUT', userContext?: any) => Promise<void>;
  sendAiFormMessage: (userInput: string) => Promise<void>;
  confirmAiFormSubmit: () => Promise<void>;
  cancelAiFormSession: () => void;
  resetAssistant: () => void;

  // Data fetchers
  getRolesCount: () => number;
  getUsersCount: () => number;
}

const UserflowContext = createContext<UserflowContextType | undefined>(undefined);

export const UserflowProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [isLoading, setIsLoading] = useState(false);
  const [isAssistantOpen, setIsAssistantOpen] = useState(false);
  const [currentMessage, setCurrentMessage] = useState('');
  const [currentStep, setCurrentStep] = useState<Step | null>(null);
  const [currentForm, setCurrentForm] = useState<any | null>(null);
  const [aiFormSession, setAiFormSession] = useState<AiFormSession | null>(null);
  const [isAiFormAwaitingConfirmation, setIsAiFormAwaitingConfirmation] = useState(false);
  const [currentStage, setCurrentStage] = useState<StageId>('initial_check');
  const [rolesCount, setRolesCount] = useState(0);
  const [usersCount, setUsersCount] = useState(0);
  const [messageHistory, setMessageHistory] = useState<Array<{ type: 'ai' | 'user' | 'system'; content: string; timestamp: Date }>>([]);

  /**
   * Initialize the assistant - check user status and load appropriate flow
   */
  const initializeAssistant = useCallback(async () => {
    setIsLoading(true);
    try {
      console.log('[UserflowContext] Initializing assistant...');

      // Step 1: Get next pending flow (handles status checking + requirements logic)
      const initResult = await assistantManager.initializeAssistant();

      console.log('[UserflowContext] Assistant init result:', initResult);

      // Step 1.5: Show greeting message first
      if (initResult.greetingMessage) {
        setCurrentMessage(initResult.greetingMessage);
        addMessageToHistory('ai', initResult.greetingMessage);
      }

      if (!initResult.flowDefinition) {
        // No pending flows - all setup complete, greeting + completion message shown
        if (initResult.message) {
          addMessageToHistory('ai', initResult.message);
        }
        setIsAssistantOpen(true);
        return;
      }

      // Step 2: Execute the first step of the flow (with automatic chaining of check steps)
      const firstFlowKey = Object.keys(initResult.flowDefinition.flows)[0];
      const firstFlow = initResult.flowDefinition.flows[firstFlowKey];

      if (!firstFlow || !firstFlow.steps || firstFlow.steps.length === 0) {
        console.error('[UserflowContext] No steps found in flow');
        setCurrentMessage('Error loading flow.');
        setIsAssistantOpen(true);
        return;
      }

      // Loop through check steps automatically until reaching a meaningful action
      let stepToExecute = firstFlow.steps[0];
      while (stepToExecute) {
        const execResult = await userflowEngine.executeStep(stepToExecute, {
          flowDefinition: initResult.flowDefinition,
          rolesCount: initResult.userStatus.rolesSetup.rolesCount,
          usersCount: initResult.userStatus.usersInvited.usersCount,
        });

        console.log('[UserflowContext] Step execution result:', execResult);

        if (!execResult.success) {
          console.error('[UserflowContext] Step execution failed:', execResult.message);
          addMessageToHistory('system', execResult.message || 'Error loading flow');
          break;
        }

        // Show message if it's a guide action
        if (execResult.action === 'guide' && execResult.data?.message) {
          setCurrentMessage(execResult.data.message);
          addMessageToHistory('ai', execResult.data.message);
        }

        // Start AI form if it's an ai_form action
        if (execResult.action === 'ai_form') {
          const aiFormConfig = execResult.data;
          await startAiFormSession(
            aiFormConfig.metadataKey,
            aiFormConfig.endpoint,
            aiFormConfig.method,
            { userName: 'User', userRole: 'admin', tenantName: 'Organization' }
          );
        }

        // If this is a check action, continue looping with nextStep (silent execution)
        if (execResult.action === 'check' && execResult.nextStep) {
          console.log('[UserflowContext] Check step completed on init, continuing to next step silently');
          stepToExecute = execResult.nextStep;
          continue;
        }

        // For non-check actions, set the step and stop looping
        if (execResult.nextStep) {
          setCurrentStep(execResult.nextStep);
        } else {
          setCurrentStep(null);
        }
        break;
      }

      // Always show the assistant
      setIsAssistantOpen(true);
    } catch (error) {
      console.error('[UserflowContext] Failed to initialize assistant:', error);
      addMessageToHistory('system', 'Failed to initialize assistant. Please refresh the page.');
      setCurrentMessage('Something went wrong. Please try again.');
    } finally {
      setIsLoading(false);
    }
  }, []);

  /**
   * Execute the next step in the workflow
   * Automatically chains check steps together without UI until a meaningful action is reached
   */
  const executeNextStep = useCallback(async () => {
    if (!currentStep) {
      console.warn('[UserflowContext] No current step to execute');
      return;
    }

    setIsLoading(true);
    let stepToExecute = currentStep;

    try {
      // Loop: keep executing steps if they're checks (silent, no UI)
      // Stop when we reach a meaningful action (guide, ai_form, confirm, etc.)
      while (stepToExecute) {
        // Re-fetch fresh status for each iteration
        const initResult = await assistantManager.initializeAssistant();

        const result = await userflowEngine.executeStep(stepToExecute, {
          flowDefinition: initResult.flowDefinition,
          rolesCount: initResult.userStatus.rolesSetup.rolesCount,
          usersCount: initResult.userStatus.usersInvited.usersCount,
        });

        if (!result.success) {
          console.error('[UserflowContext] Step execution failed:', result.message);
          addMessageToHistory('system', result.message || 'Something went wrong');
          break;
        }

        // Display message if it's a guide action
        if (result.action === 'guide' && result.data?.message) {
          setCurrentMessage(result.data.message);
          addMessageToHistory('ai', result.data.message);
        }

        // Display form if it's a form action
        if (result.action === 'form') {
          setCurrentForm(result.data);
        }

        // Start AI form session if it's an ai_form action
        if (result.action === 'ai_form') {
          const aiFormConfig = result.data;
          await startAiFormSession(
            aiFormConfig.metadataKey,
            aiFormConfig.endpoint,
            aiFormConfig.method,
            { userName: 'User', userRole: 'admin', tenantName: 'Organization' }
          );
        }

        // Display confirm options if it's a confirm action
        if (result.action === 'confirm') {
          setCurrentMessage(result.data?.message);
          addMessageToHistory('ai', result.data?.message);
        }

        // If this is a check action, continue the loop with the nextStep (silent execution)
        if (result.action === 'check' && result.nextStep) {
          console.log(`[UserflowContext] Check step completed, continuing to next step silently`);
          stepToExecute = result.nextStep;
          continue; // Loop to next iteration
        }

        // For non-check actions, set the step and stop looping
        if (result.nextStep) {
          setCurrentStep(result.nextStep);
        } else {
          setCurrentStep(null);
        }

        setCurrentStage(userflowEngine.getCurrentStage());
        break; // Stop looping
      }
    } catch (error) {
      console.error('[UserflowContext] Error executing next step:', error);
      addMessageToHistory('system', 'Error executing step');
    } finally {
      setIsLoading(false);
    }
  }, [currentStep]);

  /**
   * Submit form data
   */
  const submitForm = useCallback(async (formData: any) => {
    if (!currentForm) return;

    setIsLoading(true);
    try {
      const { endpoint, method } = currentForm;

      // Call API to submit form using the apiClient
      let response;
      if (method === 'POST') {
        response = await apiClient.post(endpoint, formData);
      } else if (method === 'PUT') {
        response = await apiClient.put(endpoint, formData);
      }

      if (response?.isSuccess || response?.success) {
        addMessageToHistory('system', 'Form submitted successfully');
        setCurrentForm(null);

        // Continue to next step (status will be re-checked in executeNextStep)
        await executeNextStep();
      } else {
        const errorMsg = response?.message || 'Failed to submit form';
        addMessageToHistory('system', errorMsg);
      }
    } catch (error) {
      console.error('[UserflowContext] Error submitting form:', error);
      addMessageToHistory('system', (error as any)?.message || 'Error submitting form');
    } finally {
      setIsLoading(false);
    }
  }, [currentForm, executeNextStep]);

  /**
   * Start an AI form session for conversational form filling
   */
  const startAiFormSession = useCallback(async (metadataKey: string, endpoint: string, method: 'POST' | 'PUT', userContext?: any) => {
    setIsLoading(true);
    try {
      console.log('[UserflowContext] Starting AI form session:', metadataKey);

      // Get form metadata from registry
      const metadata = getAiFormMetadata(metadataKey);
      if (!metadata) {
        throw new Error(`Form metadata not found: ${metadataKey}`);
      }

      // Resolve dynamic options (roles, departments, etc.)
      const resolvedOptions = await aiFormService.resolveFormOptions(metadata.fields);

      // Initialize session with empty turns to get first question
      const newSession: AiFormSession = {
        formId: metadata.formId,
        metadata,
        resolvedOptions,
        turns: [],
        collectedData: {},
        endpoint,
        method,
      };

      // Call AI to get the first question
      const aiResponse = await aiFormService.callFormAssistAI({
        formId: metadata.formId,
        fields: metadata.fields,
        turns: [],
        collectedData: {},
        userContext: userContext || { userName: 'User', userRole: 'admin', tenantName: 'Organization' },
      });

      // Add first AI message to turns and display
      newSession.turns.push({ role: 'assistant', content: aiResponse });
      setAiFormSession(newSession);
      setCurrentMessage(aiResponse);
      addMessageToHistory('ai', aiResponse);

    } catch (error) {
      console.error('[UserflowContext] Error starting AI form session:', error);
      addMessageToHistory('system', (error as any)?.message || 'Error initializing AI form');
      setCurrentMessage('Error initializing form. Please try again.');
    } finally {
      setIsLoading(false);
    }
  }, []);

  /**
   * Send a user message in the AI form session and get next AI response
   */
  const sendAiFormMessage = useCallback(async (userInput: string) => {
    if (!aiFormSession) {
      console.warn('[UserflowContext] No active AI form session');
      return;
    }

    setIsLoading(true);
    try {
      // Add user message to turns and history
      const updatedSession = { ...aiFormSession };
      updatedSession.turns.push({ role: 'user', content: userInput });
      setAiFormSession(updatedSession);
      addMessageToHistory('user', userInput);

      // Call AI with updated turns
      const aiResponse = await aiFormService.callFormAssistAI({
        formId: updatedSession.formId,
        fields: updatedSession.metadata.fields,
        turns: updatedSession.turns,
        collectedData: updatedSession.collectedData,
        userContext: { userName: 'User', userRole: 'admin', tenantName: 'Organization' },
      });

      // Check if AI is signaling form completion with submit action
      if (aiFormService.containsSubmitAction(aiResponse)) {
        // Extract the structured data
        const submitData = aiFormService.extractSubmitData(aiResponse);
        if (submitData) {
          updatedSession.collectedData = submitData;
          setAiFormSession(updatedSession);
          setIsAiFormAwaitingConfirmation(true);
          setCurrentMessage('Form is ready to submit. Confirm to proceed.');
          addMessageToHistory('ai', 'All required fields collected. Ready to submit!');
          return;
        }
      }

      // Add AI response to turns and display
      updatedSession.turns.push({ role: 'assistant', content: aiResponse });
      setAiFormSession(updatedSession);
      setCurrentMessage(aiResponse);
      addMessageToHistory('ai', aiResponse);

    } catch (error) {
      console.error('[UserflowContext] Error sending AI form message:', error);
      addMessageToHistory('system', (error as any)?.message || 'Error processing message');
      setCurrentMessage('Error processing your input. Please try again.');
    } finally {
      setIsLoading(false);
    }
  }, [aiFormSession]);

  /**
   * Confirm and submit the collected form data
   */
  const confirmAiFormSubmit = useCallback(async () => {
    if (!aiFormSession) {
      console.warn('[UserflowContext] No active AI form session');
      return;
    }

    setIsLoading(true);
    try {
      // Map data for submission (apply any necessary transformations)
      const dataToSubmit = aiFormService.mapDataForSubmission(aiFormSession.formId, aiFormSession.collectedData);

      // Submit via API
      let response;
      if (aiFormSession.method === 'POST') {
        response = await apiClient.post(aiFormSession.endpoint, dataToSubmit);
      } else if (aiFormSession.method === 'PUT') {
        response = await apiClient.put(aiFormSession.endpoint, dataToSubmit);
      }

      if (response?.isSuccess || response?.success) {
        addMessageToHistory('system', 'Form submitted successfully');
        setAiFormSession(null);
        setIsAiFormAwaitingConfirmation(false);
        setCurrentMessage('');

        // Continue to next step
        await executeNextStep();
      } else {
        const errorMsg = response?.message || 'Failed to submit form';
        addMessageToHistory('system', errorMsg);
        setCurrentMessage('Error: ' + errorMsg);
        setIsAiFormAwaitingConfirmation(false);
      }
    } catch (error) {
      console.error('[UserflowContext] Error confirming form submission:', error);
      addMessageToHistory('system', (error as any)?.message || 'Error submitting form');
      setCurrentMessage('Error submitting form. Please try again.');
    } finally {
      setIsLoading(false);
    }
  }, [aiFormSession]);

  /**
   * Cancel the AI form session
   */
  const cancelAiFormSession = useCallback(() => {
    setAiFormSession(null);
    setIsAiFormAwaitingConfirmation(false);
    setCurrentMessage('');
  }, []);

  /**
   * Handle confirm option selection
   */
  const selectConfirmOption = useCallback(async (optionValue: string) => {
    addMessageToHistory('user', `Selected: ${optionValue}`);

    // Find the next step based on option
    if (currentStep?.confirm) {
      const selectedOption = currentStep.confirm.options.find(opt => opt.value === optionValue);
      if (selectedOption?.nextStep) {
        const nextStep = userflowEngine.getFlowById(userflowEngine.getCurrentFlow()?.id || '')?.steps.find(
          s => s.id === selectedOption.nextStep
        );
        if (nextStep) {
          setCurrentStep(nextStep);
          await executeNextStep();
        }
      }
    }
  }, [currentStep, executeNextStep]);

  /**
   * Add message to history
   */
  const addMessageToHistory = (type: 'ai' | 'user' | 'system', content: string) => {
    setMessageHistory(prev => [...prev, { type, content, timestamp: new Date() }]);
  };

  /**
   * Open/Close assistant
   */
  const openAssistant = () => setIsAssistantOpen(true);
  const closeAssistant = () => {
    setIsAssistantOpen(false);
  };

  /**
   * Reset assistant (for testing or re-onboarding)
   */
  const resetAssistant = () => {
    setCurrentMessage('');
    setCurrentStep(null);
    setCurrentForm(null);
    setAiFormSession(null);
    setIsAiFormAwaitingConfirmation(false);
    setCurrentStage('initial_check');
    setRolesCount(0);
    setUsersCount(0);
    setMessageHistory([]);
    setIsAssistantOpen(false);
  };

  /**
   * Get counts
   */
  const getRolesCount = () => rolesCount;
  const getUsersCount = () => usersCount;

  const value: UserflowContextType = {
    isLoading,
    isAssistantOpen,
    currentMessage,
    currentStep,
    currentForm,
    aiFormSession,
    isAiFormAwaitingConfirmation,
    currentStage,
    rolesCount,
    usersCount,
    messageHistory,
    openAssistant,
    closeAssistant,
    initializeAssistant,
    executeNextStep,
    submitForm,
    selectConfirmOption,
    startAiFormSession,
    sendAiFormMessage,
    confirmAiFormSubmit,
    cancelAiFormSession,
    resetAssistant,
    getRolesCount,
    getUsersCount,
  };

  return <UserflowContext.Provider value={value}>{children}</UserflowContext.Provider>;
};

/**
 * Hook to use userflow context
 */
export const useUserflow = () => {
  const context = useContext(UserflowContext);
  if (!context) {
    throw new Error('useUserflow must be used within UserflowProvider');
  }
  return context;
};
